/**
 * DashboardConfig — Modal with 2 tabs:
 *   1. Seções (toggle/reorder dashboard sections)
 *   2. Integrações (manage API keys inline)
 */
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar } from './ui';
import { T } from '../styles/tokens';
import { Button } from './ui';

/* ═══ Section Config (tab 1) ═══ */
export interface DashSection {
  id: string;
  label: string;
  icon: string;
  enabled: boolean;
}

const DEFAULT_SECTIONS: DashSection[] = [
 { id: 'actions', label: 'Ações Rápidas', icon: '', enabled: true },
 { id: 'categories', label: 'Categorias', icon: '', enabled: true },
 { id: 'deploys', label: 'Últimos Deploys', icon: '', enabled: true },
 { id: 'files', label: 'Arquivos Recentes', icon: '', enabled: true },
];

const STORAGE_KEY = 'dashboard_sections';

export function loadSections(): DashSection[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as DashSection[];
      const ids = new Set(parsed.map(s => s.id));
      const merged = [...parsed];
      for (const d of DEFAULT_SECTIONS) {
        if (!ids.has(d.id)) merged.push(d);
      }
      return merged;
    }
  } catch (_) {}
  return DEFAULT_SECTIONS;
}

function saveSections(sections: DashSection[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
}

/* ═══ Integration Providers ═══ */
const PROVIDERS = [
 { id: 'trello', name: 'Trello', icon: '', color: '#0079BF', fields: [
    { key: 'api_key', label: 'API Key', type: 'text', placeholder: 'Trello API Key' },
    { key: 'token', label: 'Token', type: 'password', placeholder: 'Trello Token' },
  ]},
 { id: 'cloudflare', name: 'Cloudflare', icon: '', color: '#F6821F', fields: [
    { key: 'api_token', label: 'API Token', type: 'password', placeholder: 'Cloudflare API Token' },
    { key: 'account_id', label: 'Account ID', type: 'text', placeholder: 'Account ID' },
  ]},
 { id: 'github', name: 'GitHub', icon: '', color: T.purple, fields: [
    { key: 'token', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_...' },
    { key: 'username', label: 'Username', type: 'text', placeholder: 'myuser' },
  ]},
 { id: 'sendgrid', name: 'SendGrid', icon: '', color: '#1A82E2', fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'SG....' },
  ]},
 { id: 'anthropic', name: 'Anthropic', icon: '', color: T.warning, fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'sk-ant-...' },
  ]},
 { id: 'dynadot', name: 'Dynadot', icon: '', color: T.primaryHover, fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Dynadot API Key' },
  ]},
 { id: 'google', name: 'Google / Gmail', icon: '', color: T.danger, fields: [
    { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Google Client ID' },
    { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'GOCSPX-...' },
    { key: 'refresh_token', label: 'Refresh Token', type: 'password', placeholder: '1//0h...' },
  ]},
 { id: 'resend', name: 'Resend', icon: '', color: T.bg, fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 're_...' },
  ]},
 { id: 'custom_smtp', name: 'SMTP Customizado', icon: '', color: T.textMuted, fields: [
    { key: 'host', label: 'Host', type: 'text', placeholder: 'smtp.example.com' },
    { key: 'port', label: 'Porta', type: 'text', placeholder: '587' },
    { key: 'username', label: 'Usuário', type: 'text', placeholder: 'user@example.com' },
    { key: 'password', label: 'Senha', type: 'password', placeholder: '••••••••' },
  ]},
];

interface IntegrationRecord {
  id: string;
  provider: string;
  key_name: string;
  preview: string;
  status: string;
  updated_at: string;
}

/* ═══ Theme ═══ */
const _T = {
  bg: T.bg,
  card: T.bgCard,
  cardAlt: '#0D1220',
  primary: T.primary,
  indigo: T.accent,
  cyan: T.primary,
  text: T.text,
  muted: T.textSecondary,
  dim: T.textMuted,
  success: T.success,
  warning: T.warning,
  error: T.danger,
  border: T.border,
  font: "'Manrope', system-ui, sans-serif",
};

/* ═══ Main Modal ═══ */
interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (sections: DashSection[]) => void;
}

export function DashboardConfigModal({ open, onClose, onSave }: Props) {
  const [tab, setTab] = useState<'sections' | 'integrations'>('sections');
  const [sections, setSections] = useState<DashSection[]>(() => loadSections());

  // Integrations state
  const [integrations, setIntegrations] = useState<IntegrationRecord[]>([]);
  const [intLoading, setIntLoading] = useState(false);
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [intMsg, setIntMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (open && tab === 'integrations') loadIntegrations();
  }, [open, tab]);

  if (!open) return null;

  async function loadIntegrations() {
    setIntLoading(true);
    try {
      const data = await api.get<any>('/integrations');
      setIntegrations(data.integrations ?? []);
    } catch (e) {
      setIntMsg({ type: 'err', text: (e as Error).message });
    } finally {
      setIntLoading(false);
    }
  }

  // Section helpers
  const toggle = (id: string) => setSections(p => p.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));
  const moveUp = (i: number) => { if (i <= 0) return; setSections(p => { const a = [...p]; [a[i-1],a[i]] = [a[i],a[i-1]]; return a; }); };
  const moveDown = (i: number) => { setSections(p => { if (i >= p.length-1) return p; const a = [...p]; [a[i],a[i+1]] = [a[i+1],a[i]]; return a; }); };
  const handleSave = () => { saveSections(sections); onSave(sections); onClose(); };
  const handleReset = () => setSections(DEFAULT_SECTIONS);

  // Integration helpers
  async function saveIntegration(providerId: string, form: HTMLFormElement) {
    const fd = new FormData(form);
    const credentials: Record<string, string> = {};
    for (const [k, v] of fd.entries()) { if (v && typeof v === 'string' && v.trim()) credentials[k] = v.trim(); }
    if (!Object.keys(credentials).length) { setIntMsg({ type: 'err', text: 'Preencha pelo menos um campo' }); return; }
    setSaving(providerId);
    setIntMsg(null);
    try {
      await api.post('/integrations/save', { provider: providerId, credentials });
 setIntMsg({ type: 'ok', text: `${providerId} salvo com sucesso! ` });
      form.reset();
      setExpandedProvider(null);
      await loadIntegrations();
    } catch (e) { setIntMsg({ type: 'err', text: (e as Error).message }); }
    finally { setSaving(null); }
  }

  async function deleteIntegration(provider: string, keyName: string) {
    if (!confirm(`Remover ${provider}/${keyName}?`)) return;
    try {
      await api.delete(`/integrations/${provider}/${keyName}`);
      setIntMsg({ type: 'ok', text: `${provider}/${keyName} removido` });
      await loadIntegrations();
    } catch (e) { setIntMsg({ type: 'err', text: (e as Error).message }); }
  }

  const tabs = [
 { id: 'sections' as const, label: 'Seções', icon: '' },
 { id: 'integrations' as const, label: 'Integrações', icon: '' },
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: _T.bgOverlay,
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        animation: 'dcFadeIn 0.2s ease',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: _T.card, borderRadius: 20, maxWidth: 520, width: '94%',
          border: `1px solid ${T.border}`, boxShadow: `0 24px 80px ${_T.blackAlpha50}`,
          animation: 'dcSlideUp 0.25s ease', fontFamily: _T.font,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
        onClick={(e: any) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0 }}>
 Configurar Dashboard
              </h3>
              <p style={{ fontSize: 12, color: _T.dim, margin: '4px 0 0' }}>
                Personalize seções e integrações
              </p>
            </div>
            <Button variant="ghost" onClick={onClose} style={{
              background: T.border, border: 'none', color: _T.muted,
              fontSize: 16, cursor: 'pointer', width: 32, height: 32, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}></Button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, background: _T.cardAlt, borderRadius: 10, padding: 3 }}>
            {tabs.map(t => (
              <Button variant="ghost"
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none',
                  background: tab === t.id ? T.primary : 'transparent',
                  color: tab === t.id ? '#fff' : _T.muted,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: _T.font,
                  transition: 'all 0.2s',
                }}
              >
                {t.icon} {t.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 28px 24px', minHeight: 0 }}>

          {/* ── TAB: Seções ── */}
          {tab === 'sections' && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {sections.map((s, i) => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', borderRadius: 12,
                    background: s.enabled ? T.successBg : T.bgSubtle,
                    border: `1px solid ${s.enabled ? T.successBg : T.border}`,
                    transition: 'all 0.2s',
                  }}>
                    <Button variant="ghost"
                      onClick={() => toggle(s.id)}
                      style={{
                        width: 40, height: 22, borderRadius: 12, border: 'none',
                        background: s.enabled ? T.success : _T.bgModal, cursor: 'pointer',
                        position: 'relative', flexShrink: 0, transition: 'background 0.2s',
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: 2, left: s.enabled ? 20 : 2,
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s', boxShadow: `0 1px 3px ${_T.blackAlpha30}`,
                      }} />
                    </Button>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{s.icon}</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: s.enabled ? T.text : _T.dim }}>{s.label}</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button variant="ghost" onClick={() => moveUp(i)} disabled={i===0} style={{
                        background: 'none', border: `1px solid ${T.border}`, borderRadius: 4,
                        width: 24, height: 20, cursor: i===0?'default':'pointer',
                        color: i===0?_T.dim:T.text, fontSize: 10, opacity: i===0?0.3:1,
                        display:'flex', alignItems:'center', justifyContent:'center',
                      }}>▲</Button>
                      <Button variant="ghost" onClick={() => moveDown(i)} disabled={i===sections.length-1} style={{
                        background: 'none', border: `1px solid ${T.border}`, borderRadius: 4,
                        width: 24, height: 20, cursor: i===sections.length-1?'default':'pointer',
                        color: i===sections.length-1?_T.dim:T.text, fontSize: 10,
                        opacity: i===sections.length-1?0.3:1,
                        display:'flex', alignItems:'center', justifyContent:'center',
                      }}>▼</Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
                <Button variant="ghost" onClick={handleReset} style={{
                  padding: '8px 16px', background: 'none', border: `1px solid ${T.border}`,
                  borderRadius: 8, color: _T.dim, fontSize: 12, cursor: 'pointer', fontFamily: _T.font,
 }}>Restaurar padrão</Button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" onClick={onClose} style={{
                    padding: '8px 20px', background: T.bgCard,
                    color: T.text, border: `1px solid ${T.border}`, borderRadius: 8,
                    cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: _T.font,
                  }}>Cancelar</Button>
                  <Button variant="ghost" onClick={handleSave} style={{
                    padding: '8px 20px', background: T.primary, color: T.text, border: 'none',
                    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: _T.font,
 }}>Salvar</Button>
                </div>
              </div>
            </div>
          )}

          {/* ── TAB: Integrações ── */}
          {tab === 'integrations' && (
            <div>
              {/* Message */}
              {intMsg && (
                <div style={{
                  padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 12, fontFamily: _T.font,
                  background: intMsg.type === 'ok' ? T.successBg : T.dangerBg,
                  border: `1px solid ${intMsg.type === 'ok' ? T.successBg : T.dangerBg}`,
                  color: intMsg.type === 'ok' ? T.success : _T.error,
                }}>
                  {intMsg.text}
                </div>
              )}

              {intLoading ? (
                <div style={{ textAlign: 'center', padding: 24, color: _T.muted, fontSize: 13 }}>
 <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {PROVIDERS.map(p => {
                    const keys = integrations.filter(i => i.provider === p.id);
                    const configured = keys.length > 0;
                    const isExpanded = expandedProvider === p.id;

                    return (
                      <div key={p.id} style={{
                        borderRadius: 14, overflow: 'hidden',
                        border: `1px solid ${configured ? T.successBg : T.border}`,
                        background: isExpanded ? T.primaryBg : T.bgSubtle,
                        transition: 'all 0.2s',
                      }}>
                        {/* Provider row */}
                        <Button variant="ghost"
                          onClick={() => setExpandedProvider(isExpanded ? null : p.id)}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                            padding: '12px 16px', background: 'none', border: 'none',
                            cursor: 'pointer', textAlign: 'left',
                          }}
                        >
                          <span style={{ fontSize: 20, flexShrink: 0 }}>{p.icon}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: _T.font }}>
                              {p.name}
                            </div>
                            {configured && (
                              <div style={{ fontSize: 11, color: T.success, fontFamily: _T.font, marginTop: 1 }}>
 {keys.length} chave{keys.length > 1 ? 's' : ''} configurada{keys.length > 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                          <div style={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: configured ? T.success : _T.dim,
                            flexShrink: 0,
                          }} />
                          <span style={{
                            color: _T.muted, fontSize: 12, transition: 'transform 0.2s',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                          }}>▼</span>
                        </Button>

                        {/* Expanded content */}
                        {isExpanded && (
                          <div style={{ padding: '0 16px 16px' }}>
                            {/* Existing keys */}
                            {keys.length > 0 && (
                              <div style={{
                                background: T.bgCard, borderRadius: 10,
                                padding: '10px 12px', marginBottom: 12,
                              }}>
                                {keys.map(k => (
                                  <div key={k.id} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '6px 0', gap: 8,
                                  }}>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: _T.font }}>
 {k.key_name}
                                      </span>
                                      <span style={{ marginLeft: 6, fontSize: 11, color: _T.dim, fontFamily: 'monospace' }}>
                                        {k.preview || '••••'}
                                      </span>
                                    </div>
                                    <Button variant="ghost"
                                      onClick={() => deleteIntegration(k.provider, k.key_name)}
                                      style={{
                                        background: T.dangerBg, border: 'none', borderRadius: 6,
                                        color: _T.error, fontSize: 11, padding: '3px 8px', cursor: 'pointer',
                                        fontFamily: _T.font, fontWeight: 600, flexShrink: 0,
                                      }}
                                    ></Button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Add/Update form */}
                            <form
                              id={`int-form-${p.id}`}
                              onSubmit={(e) => {
                                e.preventDefault();
                                saveIntegration(p.id, e.target as HTMLFormElement);
                              }}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {p.fields.map(f => (
                                  <div key={f.key}>
                                    <label style={{ fontSize: 11, color: _T.muted, marginBottom: 3, display: 'block', fontFamily: _T.font }}>
                                      {f.label}
                                    </label>
                                    <input
                                      name={f.key}
                                      type={f.type}
                                      placeholder={f.placeholder}
                                      style={{
                                        width: '100%', padding: '8px 12px', borderRadius: 8,
                                        background: T.bgCard, border: `1px solid ${T.border}`,
                                        color: T.text, fontSize: 13, fontFamily: _T.font,
                                        outline: 'none', boxSizing: 'border-box',
                                      }}
                                      onFocus={(e: any) => e.target.style.borderColor = T.primary}
                                      onBlur={(e: any) => e.target.style.borderColor = T.border}
                                    />
                                  </div>
                                ))}
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                                <Button variant="ghost"
                                  type="button"
                                  onClick={() => setExpandedProvider(null)}
                                  style={{
                                    padding: '6px 14px', background: 'none', border: `1px solid ${T.border}`,
                                    borderRadius: 8, color: _T.muted, fontSize: 12, cursor: 'pointer', fontFamily: _T.font,
                                  }}
                                >Cancelar</Button>
                                <Button variant="ghost"
                                  type="submit"
                                  disabled={saving === p.id}
                                  style={{
                                    padding: '6px 16px', background: T.primary, color: T.text,
                                    border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600,
                                    cursor: saving === p.id ? 'not-allowed' : 'pointer',
                                    opacity: saving === p.id ? 0.6 : 1, fontFamily: _T.font,
                                  }}
                                >
 {saving === p.id ? '...' : 'Salvar'}
                                </Button>
                              </div>
                            </form>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes dcFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dcSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
